import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import Breakpoint from '../../Breakpoint'
import { BadnetTournamentModel } from '../../types/badnetTournament.type'

const BadnetTournamentEvent = ({ tournament }: { tournament: BadnetTournamentModel }) => {
    const { t } = useTranslation('translation')

    const cleanHtml = (html: string | null): string => {
        if (!html) return ''
        return html
            .replace(/<[^>]*>/g, '')
            .replace(/schedule/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .trim()
    }

    const cleanDeadline = cleanHtml(tournament.deadline)
    const cleanParticipants = cleanHtml(tournament.participants)

    return (
        <TournamentContainer>
            <TournamentWrapper>
                <TournamentTitle>{tournament.title}</TournamentTitle>
                <TournamentMeta>
                    <MetaItem>
                        <MetaLabel>{t('page.badnetTournament.date')}:</MetaLabel>
                        <MetaValue>{tournament.date}</MetaValue>
                    </MetaItem>
                    <MetaItem>
                        <MetaLabel>{t('page.badnetTournament.place')}:</MetaLabel>
                        <MetaValue>{tournament.place}</MetaValue>
                    </MetaItem>
                    <MetaItem>
                        <MetaLabel>{t('page.badnetTournament.categories')}:</MetaLabel>
                        <MetaValue>{tournament.categories}</MetaValue>
                    </MetaItem>
                    <MetaItem>
                        <MetaLabel>{t('page.badnetTournament.classifications')}:</MetaLabel>
                        <MetaValue>{tournament.classifications}</MetaValue>
                    </MetaItem>
                    {cleanDeadline && (
                        <MetaItem>
                            <MetaLabel>{t('page.badnetTournament.deadline')}:</MetaLabel>
                            <MetaValue>{cleanDeadline}</MetaValue>
                        </MetaItem>
                    )}
                    {cleanParticipants && (
                        <MetaItem>
                            <MetaLabel>{t('page.badnetTournament.participants')}:</MetaLabel>
                            <MetaValue>{cleanParticipants}</MetaValue>
                        </MetaItem>
                    )}
                </TournamentMeta>
                {tournament.promoted && <PromotedBadge>{t('page.badnetTournament.promoted')}</PromotedBadge>}
            </TournamentWrapper>
            <LinkWrapper>
                <LinkTournament href={tournament.url} target="_blank" rel="noreferrer">
                    {t('page.badnetTournament.register')}
                </LinkTournament>
                {tournament.helloassoUrl && (
                    <HelloAssoLink href={tournament.helloassoUrl} target="_blank" rel="noreferrer">
                        {t('page.badnetTournament.helloasso')}
                    </HelloAssoLink>
                )}
            </LinkWrapper>
        </TournamentContainer>
    )
}

const LinkWrapper = styled.div`
    position: absolute;
    bottom: 0;
    right: 8px;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        position: inherit;
    }
`

const LinkTournament = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--primary-color);
    color: white;
    text-decoration: none;
    height: 36px;
    border-radius: 4px;
    transition: all 0.4s;
    margin-bottom: 8px;
    padding: 0 8px;

    &:hover {
        opacity: 0.7;
        color: white;
    }
`

const HelloAssoLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #00a3e0;
    color: white;
    text-decoration: none;
    height: 36px;
    border-radius: 4px;
    transition: all 0.4s;
    margin-bottom: 8px;
    padding: 0 8px;

    &:hover {
        opacity: 0.7;
        color: white;
    }
`

const TournamentContainer = styled.article`
    position: relative;
    width: 100%;
    min-height: 200px;
    display: flex;
    background: white;
    padding: 16px;
    border-radius: 6px;
    margin-right: 5px;
    margin-bottom: 24px;
    box-sizing: border-box;
    overflow: hidden;

    &:hover {
        box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.3);
    }

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        margin-bottom: 16px;
        flex-direction: column;
        min-height: auto;
    }
`

const TournamentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-right: 16px;
    margin-bottom: 8px;
`

const TournamentTitle = styled.h2`
    font-size: 2rem;
    margin-top: 0;
    margin-bottom: 16px;
    color: var(--primary-color);
    font-family: 'Roboto Semi Bold';
`

const TournamentMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const MetaItem = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`

const MetaLabel = styled.span`
    font-weight: bold;
    color: #666;
    min-width: 120px;
`

const MetaValue = styled.span`
    color: #333;
`

const PromotedBadge = styled.span`
    display: inline-block;
    background-color: rgba(180, 0, 0, 0.1);
    color: var(--primary-color);
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.9rem;
    font-weight: bold;
    margin-top: 8px;
    align-self: flex-start;
`

export default BadnetTournamentEvent
